// @flow

import type { FluxStandardAction } from 'flux-standard-action';
import * as ACTIONS from '../constants/actions';
import * as LocationModel from '../models/location';

export type ReducerType = LocationModel.t[];

const INITIAL_STATE = [];

export default function locationsReducer(
  state: ReducerType = INITIAL_STATE,
  { type, payload }: FluxStandardAction<string, *>
): ReducerType {
  switch (type) {
    case ACTIONS.FETCH_RECORDINGS_SUCCESS:
      return payload.locations;

    case ACTIONS.EDIT_LOCATION_SUCCESS:
      let updatedLocations = [];
      if (state && payload && payload.id) {
        const locations = [...state];
        const index = locations.findIndex(loc => loc.id === payload.id);
        if (index >= 0) {
          updatedLocations = locations;
          updatedLocations[index] = {
            ...updatedLocations[index],
            ...payload
          };
        }
      } else {
        updatedLocations = state;
      }
      return updatedLocations;

    case ACTIONS.EDIT_LOCATION_ERROR:
      return state;

    case ACTIONS.ASSIGN_TAG_TO_LOCATION_SUCCESS: {
      if (!payload || !payload.locationId || !payload.tagId) return state;
      return state.map((l) => {
        if (l.id !== payload.locationId) return l;
        const existing = l.tags || [];
        if (existing.includes(payload.tagId)) return l;
        return { ...l, tags: [...existing, payload.tagId] };
      });
    }

    case ACTIONS.REMOVE_TAG_FROM_LOCATION_SUCCESS: {
      if (!payload || !payload.locationId || !payload.tagId) return state;
      return state.map((l) => {
        if (l.id !== payload.locationId) return l;
        const existing = l.tags || [];
        return { ...l, tags: existing.filter((id) => id !== payload.tagId) };
      });
    }

    case ACTIONS.BULK_ASSIGN_TAGS_SUCCESS: {
      const results = (payload && payload.results) || [];
      if (results.length === 0) return state;
      const addedByLocId = {};
      results.forEach((r) => {
        if (r && r.locationId) addedByLocId[r.locationId] = r.added || [];
      });
      return state.map((l) => {
        const added = addedByLocId[l.id];
        if (!added || added.length === 0) return l;
        const next = new Set([...(l.tags || []), ...added]);
        return { ...l, tags: Array.from(next) };
      });
    }

    case ACTIONS.BULK_REMOVE_TAGS_SUCCESS: {
      const results = (payload && payload.results) || [];
      if (results.length === 0) return state;
      const removedByLocId = {};
      results.forEach((r) => {
        if (r && r.locationId) removedByLocId[r.locationId] = r.removed || [];
      });
      return state.map((l) => {
        const removed = removedByLocId[l.id];
        if (!removed || removed.length === 0) return l;
        return { ...l, tags: (l.tags || []).filter((id) => !removed.includes(id)) };
      });
    }

    case ACTIONS.DELETE_TAG_SUCCESS: {
      if (!payload || !payload.id) return state;
      return state.map((l) =>
        !l.tags || !l.tags.includes(payload.id)
          ? l
          : { ...l, tags: l.tags.filter((id) => id !== payload.id) },
      );
    }

    case ACTIONS.HYDRATE_LOCATION_TAGS: {
      const tagsByLocationId = (payload && payload.tagsByLocationId) || {};
      return state.map((l) => {
        const tags = tagsByLocationId[l.id];
        if (tags === undefined) return l;
        return { ...l, tags };
      });
    }

    default:
      return state;
  }
}
